import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { apiClient } from "../../services/apiClientService";
import { getLineItemUnitPrice } from "../../utils/productVariants";

/** Distance from viewport bottom to the bar’s bottom edge = header tab row + safe area (no padding “dead zone” under the pill). */
const MOBILE_CART_ABOVE_NAV =
  "calc(0.375rem + 46px + max(0.25rem, env(safe-area-inset-bottom, 0px)))";

/**
 * Mobile-only floating cart strip above the header bottom nav.
 * The bar is `position:absolute` with `bottom: …` so there is no extra transparent band below the white card (that band looked like a gap over the product list / footer).
 */
const MobileFloatingCartBar = () => {
  const { cart, setCartDrawerOpen, clearCart } = useCart();
  const location = useLocation();
  const [storefrontVendor, setStorefrontVendor] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const hidePaths = ["/user-login", "/user-signup"];
  const hideBar = hidePaths.includes(location.pathname);
  const onCheckout = location.pathname.startsWith("/checkout");

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await apiClient("GET", "/api/vendors/storefront", null, {
          skipLoading: true,
        });
        if (!mounted) return;
        setStorefrontVendor(res?.singleVendor ? res.vendor : null);
      } catch {
        if (mounted) setStorefrontVendor(null);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const cartItemCount = cart.reduce((n, { quantity }) => n + quantity, 0);
  const cartSubtotal = cart.reduce(
    (sum, { product, quantity, variantOptionId }) =>
      sum + getLineItemUnitPrice(product, variantOptionId) * quantity,
    0,
  );
  const firstCartImage = cart[0]?.product?.images?.[0];
  const storeLabel = storefrontVendor?.storeName || "Your order";

  if (cart.length === 0 || hideBar || onCheckout) {
    return null;
  }

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] md:hidden">
      <div
        className="pointer-events-auto absolute left-1/2 w-[min(100vw-1.5rem,32rem)] -translate-x-1/2"
        style={{ bottom: MOBILE_CART_ABOVE_NAV }}
      >
        <div className="flex items-center gap-2 rounded-2xl border border-stone-200/90 bg-white py-2 pl-2 pr-2 shadow-[0_8px_28px_rgba(0,0,0,0.15)]">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <Link
              to="/shop"
              className="relative h-11 w-11 shrink-0 overflow-hidden rounded-full border border-stone-100 bg-gradient-to-br from-orange-100 to-amber-100 ring-1 ring-black/5"
            >
              {(firstCartImage || storefrontVendor?.storeLogo) && (
                <img
                  src={firstCartImage || storefrontVendor?.storeLogo}
                  alt=""
                  className="h-full w-full object-cover"
                />
              )}
            </Link>
            <div className="min-w-0 flex-1">
              <Link
                to="/shop"
                className="block truncate text-sm font-bold text-stone-900 hover:text-orange-700"
              >
                {storeLabel}
              </Link>
              <button
                type="button"
                className="mt-0.5 flex items-center gap-0.5 text-left text-xs font-semibold text-rose-600 hover:text-rose-700"
                onClick={() => setCartDrawerOpen(true)}
              >
                {cartItemCount} {cartItemCount === 1 ? "item" : "items"}
                <span className="text-stone-400"> · </span>
                View cart
                <ChevronRight className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setCartDrawerOpen(true)}
            className="flex shrink-0 flex-col items-center justify-center rounded-xl bg-gradient-to-br from-rose-600 to-red-600 px-3 py-2 text-center text-white shadow-md transition active:scale-[0.98] hover:from-rose-700 hover:to-red-700"
          >
            <span className="text-sm font-extrabold leading-none tabular-nums">
              ₹{cartSubtotal.toFixed(0)}
            </span>
            <span className="mt-0.5 text-[10px] font-bold leading-none">VIEW CART</span>
          </button>

          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-500 shadow-sm transition hover:bg-stone-50 hover:text-rose-600 active:scale-[0.98]"
            aria-label="Clear cart"
            title="Clear cart"
          >
            <X className="h-5 w-5" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {confirmOpen ? (
        <div className="pointer-events-auto fixed inset-0 z-[220] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/45 backdrop-blur-sm"
            onClick={() => (clearing ? null : setConfirmOpen(false))}
            aria-hidden
          />
          <div className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-2xl ring-1 ring-black/5">
            <div className="px-5 pt-5 pb-4">
              <p className="text-sm font-extrabold text-stone-900">
                Clear cart?
              </p>
              <p className="mt-1 text-sm text-stone-600">
                Do you really want to clear the cart like this?
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-stone-100 bg-stone-50/60 px-4 py-3">
              <button
                type="button"
                disabled={clearing}
                onClick={() => setConfirmOpen(false)}
                className="rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm font-bold text-stone-700 transition hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={clearing}
                onClick={async () => {
                  try {
                    setClearing(true);
                    await clearCart();
                    setCartDrawerOpen(false);
                    setConfirmOpen(false);
                  } finally {
                    setClearing(false);
                  }
                }}
                className="rounded-xl bg-gradient-to-r from-rose-600 to-red-600 px-4 py-2 text-sm font-extrabold text-white shadow-md shadow-rose-400/30 transition hover:from-rose-700 hover:to-red-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {clearing ? "Clearing…" : "Yes, clear"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body,
  );
};

export default MobileFloatingCartBar;
